import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { Input, TextArea, Button, Card } from '../common';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { Save, RefreshCcw } from 'lucide-react';
import { theme } from '../../styles/theme';

const initialFormState = {
  revision_number: '',
  title: '',
  requirement: '',
  checklist_questions: [
    { question: '', compliance_verified: false, evidence_observations: '' }
  ],
  auditor_actions: '',
  audit_sampling_methods: '',
  guidance: '',
  description_short: '',
  description_long: ''
};

const ChecklistForm = ({ checklist, onSaveSuccess, onCancel }) => {
  const [formData, setFormData] = useState(() => {
    const savedData = localStorage.getItem('checklistFormData');
    return savedData ? JSON.parse(savedData) : (checklist || initialFormState);
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saveStatus, setSaveStatus] = useState('');

  // Load initial data
  useEffect(() => {
    if (checklist) {
      const initialChecklist = {
        ...checklist,
        checklist_questions: checklist.checklist_questions || [
          { question: '', compliance_verified: false, evidence_observations: '' }
        ]
      };
      setFormData(initialChecklist);
    }
  }, [checklist]);

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('checklistFormData', JSON.stringify(formData));
  }, [formData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleQuestionChange = (index, field, value) => {
    const updatedQuestions = [...formData.checklist_questions];
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      checklist_questions: updatedQuestions
    }));
  };

  const addQuestion = () => {
    setFormData(prev => ({
      ...prev,
      checklist_questions: [
        ...prev.checklist_questions,
        { question: '', compliance_verified: false, evidence_observations: '' }
      ]
    }));
  };

  const removeQuestion = (index) => {
    setFormData(prev => ({
      ...prev,
      checklist_questions: prev.checklist_questions.filter((_, i) => i !== index)
    }));
  };

  const handleReset = () => {
    setFormData(checklist || initialFormState);
    localStorage.removeItem('checklistFormData');
  };

  const validateForm = () => {
    if (!formData.revision_number.trim()) {
      setError('Checklist Number is required');
      return false;
    }
    if (!formData.title.trim()) {
      setError('Title is required');
      return false;
    }
    if (!formData.requirement.trim()) {
      setError('Requirement is required');
      return false;
    }
    if (!formData.checklist_questions.some(q => q.question.trim())) {
      setError('At least one question is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');
    setSaveStatus('Saving...');

    try {
      // Get current user
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw sessionError;

      const userId = session?.user?.id;
      if (!userId) {
        throw new Error('User not authenticated');
      }

      // Format the data for insert/update
      const formattedData = {
        revision_number: formData.revision_number,
        title: formData.title,
        requirement: formData.requirement,
        checklist_questions: JSON.stringify(formData.checklist_questions),
        auditor_actions: formData.auditor_actions,
        audit_sampling_methods: formData.audit_sampling_methods,
        guidance: formData.guidance,
        description_short: formData.description_short,
        description_long: formData.description_long,
        created_by: userId,
        updated_by: userId
      };

      let result;
      if (checklist?.id) {
        // Update existing record
        const { data, error: updateError } = await supabase
          .from('audit_checklist')
          .update({
            ...formattedData,
            updated_by: userId
          })
          .eq('id', checklist.id)
          .select();

        if (updateError) throw updateError;
        result = data;
      } else {
        // Insert new record
        const { data, error: insertError } = await supabase
          .from('audit_checklist')
          .insert([formattedData])
          .select();

        if (insertError) throw insertError;
        result = data;
      }

      setSaveStatus('Saved successfully');
      localStorage.removeItem('checklistFormData');
      
      // Wait for the success message to be visible before navigating
      setTimeout(() => {
        onSaveSuccess();
      }, 1500);

    } catch (error) {
      console.error('Error saving checklist:', error);
      setError(`Failed to save checklist: ${error.message}`);
      setSaveStatus('Error saving');
    } finally {
      setLoading(false);
      // Clear save status after 3 seconds
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const generatePDF = async () => {
    try {
      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([612, 792]); // US Letter size
      const { width, height } = page.getSize();
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      // Add company logo placeholder
      page.drawRectangle({
        x: width - 100,
        y: height - 80,
        width: 80,
        height: 60,
        borderColor: rgb(0.7, 0.7, 0.7),
        borderWidth: 1
      });

      // Title
      page.drawText('Audit Checklist', {
        x: 50,
        y: height - 50,
        size: 24,
        font: boldFont
      });

      // Checklist Number
      page.drawText(`Checklist Number: ${formData.revision_number}`, {
        x: 50,
        y: height - 80,
        size: 12,
        font: font
      });

      // Requirement section
      page.drawText('Requirement:', {
        x: 50,
        y: height - 120,
        size: 12,
        font: boldFont
      });

      const requirementLines = formData.requirement.split('\n');
      let yOffset = height - 140;
      requirementLines.forEach(line => {
        page.drawText(line, {
          x: 50,
          y: yOffset,
          size: 10,
          font: font,
          maxWidth: width - 100
        });
        yOffset -= 15;
      });

      // Questions table
      yOffset -= 30;
      page.drawText('Checklist Questions', {
        x: 50,
        y: yOffset,
        size: 14,
        font: boldFont
      });

      yOffset -= 20;
      page.drawText('Compliance verified?', {
        x: width - 200,
        y: yOffset,
        size: 12,
        font: boldFont
      });

      yOffset -= 20;
      page.drawText('Evidence/Observations', {
        x: width - 200,
        y: yOffset,
        size: 12,
        font: boldFont
      });

      formData.checklist_questions.forEach((q, index) => {
        yOffset -= 30;

        if (yOffset < 100) {
          // Add new page if needed
          page = pdfDoc.addPage([612, 792]);
          yOffset = height - 50;
        }

        page.drawText(`${index + 1}. ${q.question}`, {
          x: 50,
          y: yOffset,
          size: 10,
          font: font,
          maxWidth: width - 250
        });

        page.drawText(q.compliance_verified ? 'Yes' : 'No', {
          x: width - 180,
          y: yOffset,
          size: 10,
          font: font
        });

        yOffset -= 20;
        const evidenceLines = q.evidence_observations.split('\n');
        evidenceLines.forEach(line => {
          page.drawText(line, {
            x: 70,
            y: yOffset,
            size: 10,
            font: font,
            maxWidth: width - 100
          });
          yOffset -= 15;
        });
      });

      // Additional sections
      const sections = [
        { title: 'Auditor Actions', content: formData.auditor_actions },
        { title: 'Audit Sampling Methods', content: formData.audit_sampling_methods },
        { title: 'Guidance', content: formData.guidance }
      ];

      sections.forEach(section => {
        yOffset -= 30;

        if (yOffset < 100) {
          page = pdfDoc.addPage([612, 792]);
          yOffset = height - 50;
        }

        page.drawText(section.title, {
          x: 50,
          y: yOffset,
          size: 14,
          font: boldFont
        });

        yOffset -= 20;
        const contentLines = section.content.split('\n');
        contentLines.forEach(line => {
          page.drawText(line, {
            x: 50,
            y: yOffset,
            size: 10,
            font: font,
            maxWidth: width - 100
          });
          yOffset -= 15;
        });
      });

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `checklist-${formData.revision_number}.pdf`;
      link.click();
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF');
    }
  };

  return (
    <Card className="p-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        <h2 className="text-2xl font-bold mb-6" style={{ color: theme.colors.primary.main }}>
          {checklist ? 'Edit Checklist' : 'Create New Checklist'}
        </h2>

        {error && (
          <div className="p-4 bg-red-50 border border-red-100 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Checklist Number"
              name="revision_number"
              value={formData.revision_number}
              onChange={handleInputChange}
              required
            />
            <Input
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
            />
          </div>

          <TextArea
            label="Requirement"
            name="requirement"
            value={formData.requirement}
            onChange={handleInputChange}
            placeholder="Insert specific requirement (copy and paste from standard, directive, regulation, etc.)"
            rows={4}
            required
          />

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium">Checklist Questions</h3>
              <Button
                type="button"
                onClick={addQuestion}
                variant="secondary"
              >
                Add Question
              </Button>
            </div>
            
            {formData.checklist_questions.map((question, index) => (
              <div key={index} className="p-4 border rounded-lg space-y-4 bg-white shadow-sm">
                <div className="flex justify-between">
                  <span className="font-medium">Question {index + 1}</span>
                  {formData.checklist_questions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeQuestion(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  )}
                </div>
                
                <TextArea
                  value={question.question}
                  onChange={(e) => handleQuestionChange(index, 'question', e.target.value)}
                  placeholder="Enter question"
                  rows={2}
                />
                
                <div className="flex items-center space-x-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={question.compliance_verified}
                      onChange={(e) => handleQuestionChange(index, 'compliance_verified', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Compliance verified?</span>
                  </label>
                </div>
                
                <TextArea
                  value={question.evidence_observations}
                  onChange={(e) => handleQuestionChange(index, 'evidence_observations', e.target.value)}
                  placeholder="Evidence and/or observations"
                  rows={2}
                />
              </div>
            ))}
          </div>

          <TextArea
            label="Auditor Actions"
            name="auditor_actions"
            value={formData.auditor_actions}
            onChange={handleInputChange}
            rows={3}
          />

          <TextArea
            label="Audit Sampling Methods"
            name="audit_sampling_methods"
            value={formData.audit_sampling_methods}
            onChange={handleInputChange}
            placeholder="Where to look and how many to look at, find, discuss or interview"
            rows={3}
          />

          <TextArea
            label="Guidance"
            name="guidance"
            value={formData.guidance}
            onChange={handleInputChange}
            rows={3}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <TextArea
              label="Short Description"
              name="description_short"
              value={formData.description_short}
              onChange={handleInputChange}
              rows={2}
            />
            <TextArea
              label="Long Description"
              name="description_long"
              value={formData.description_long}
              onChange={handleInputChange}
              rows={2}
            />
          </div>
        </div>

        {saveStatus && (
          <div className={`mt-4 text-center px-4 py-2 rounded-lg border ${
            saveStatus.includes('Error')
              ? 'bg-red-50 border-red-200 text-red-600'
              : 'bg-emerald-50 border-emerald-200 text-emerald-600'
          }`}>
            {saveStatus}
          </div>
        )}

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={handleReset}
            disabled={loading}
            icon={RefreshCcw}
          >
            Reset
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={generatePDF}
            disabled={loading}
          >
            Export PDF
          </Button>
          <Button
            type="submit"
            disabled={loading}
            icon={Save}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default ChecklistForm;
